import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import _ from 'lodash';
import { spliceOut } from 'components/sharpr/util/spliceOut';

export function CategoryManagerRoute($stateProvider) {
	'ngInject';

	$stateProvider.state('hub.loggedin.CategoryManager', {
		url: '/admin/categories',
		controllerAs: 'vm',
		controller: CategoryManagerCtrl,
		templateUrl: '/ng/hub/Category/Manager/CategoryManagerCtrl.html?' + window.BUILD,
		permission: ['manage_categories'],
		resolve: {
			style: () => AssetManager.css('/ng/hub/Category/Manager/CategoryManagerCtrl.css'),
			$title: () => 'Manage Categories',
			categories: ApiResourcify.endpoint('/categories'),
		},
		onExit: Taxonomy => {
			'ngInject';
			Taxonomy.clearCategories();
		},
	});
}

export class CategoryManagerCtrl {
	constructor(Api, modal, categories) {
		'ngInject';
		this.Api = Api;
		this.modal = modal;
		this.categories = categories;
		this.id = null;
		this.name = null;
		this.color = null;
		this.modalIsOpen = false;
	}

	/**
	 * Reset the local state variables and hide the modal.
	 */
	cancel() {
		// Because this method will be called twice on a "Cancel" button click (once for the cancel button and once for
		// the modal's on-close event), we use the modalIsOpen value to decide whether reset all the local state variables.
		if (this.modalIsOpen) {
			// Reinitialize
			this.id = null;
			this.name = null;
			this.color = null;

			// Hide.
			this.modalIsOpen = false;
		}
	}

	/**
	 * Normalize and validate the modal's form data and save changes to the database.
	 * @param data
	 */
	submit(data = {}) {
		// Normalize the submitted data.
		const id = data.id ? parseInt(data.id) : null;
		const name = data.name ? data.name.trim() : null;
		const color = data.color ? data.color.trim() : null;

		// Validate the user's inputs. An alert will be triggered for any invalid values.
		if (!name) {
			alert('Category name is missing. Please enter a new category name.');
		} else if (name.length > 50) {
			alert('Category names cannot be more than 50 characters. Please choose a different name.');
		} else if (name.indexOf(';') > -1) {
			alert('Category names cannot contain semicolons. Please choose a different name.');
		} else if (/^\d+$/.test(name)) {
			alert('Category names cannot consist of only numbers. Please choose a different name.');
		} else if (color && new RegExp('^#?[0-9a-f]{6}$', 'i').test(color) === false) {
			alert('When set, display color must be a valid hexadecimal color code.');
		} else {
			// Determine if this is an UPDATE or an INSERT type of request.
			if (id) {
				// This is an UPDATE.
				this.Api.patch(`/categories/${id}`, { name, color }).then(
					() => {
						// Find the reference to the target category and update its name and color properties.
						const target = this.categories.find(o => o.id === id);

						target.name = name;
						target.color = color;

						// NOTE: Waiting to close the modal until we've seen that the save succeeded.
						this.modalIsOpen = false;
					},
					err => {
						alert(`System error encountered: ${err ? err.toString() : 'Unknown'}`);
					}
				);
			} else {
				// This is an INSERT.
				this.Api.create('/categories', { name, color }).then(
					id => {
						if (this.Api.lastCreateResponse.status === 201) {
							this.categories.push({ id, name, color });

							// NOTE: Waiting to close the modal until we've seen that the save succeeded.
							this.modalIsOpen = false;
						} else {
							alert(`Error: Category "${name}" already exists. Please choose a different name.`);
						}
					},
					err => {
						alert(`An system error was encountered: ${err ? err.toString() : 'Unknown'}`);
					}
				);
			}
		}
	}

	/**
	 * Reinitialize and display the modal.
	 */
	addNew() {
		// Initialize
		this.id = null;
		this.name = null;
		this.color = null;

		// Display
		this.modalIsOpen = true;

		// Focus
		this.modal.attemptFocus();
	}

	/**
	 * Initialize the model with the provided category's option and display the modal.
	 * @param category
	 */
	edit(category) {
		// Initialize
		this.id = category.id;
		this.name = category.name;
		this.color = category.color;

		// Display
		this.modalIsOpen = true;

		// Focus
		this.modal.attemptFocus();
	}

	updateOrder() {
		const ids = _.pluck(this.categories, 'id');
		this.Api.post('/categories/sort', { ids });
	}

	getPostCount(catId) {
		return this.Api.get('/v3/posts/search', {
			category_id: catId,
			count_only: true,
		}).then(
			res => res.count,
			err => err
		);
	}

	/**
	 * Open the merge/delete category modal
	 * @param {Object} cat  The category to merge/delete
	 * @param {String} type  Either 'merge' or 'delete'
	 * @return {Promise<void>}  Resolves when modal opens
	 */
	async openUpdateDialog(cat, type) {
		const modal = this.modal.alert({
			escapedHtml: `Checking for posts with category <b>${_.escape(cat.name)}</b>...`,
		});
		modal.catch(() => {});
		try {
			const count = await this.getPostCount(cat.id);
			modal.close();
			if (count === null) {
				throw new Error('getPostCount() returned null');
			}
			if (count > 0) {
				// open modal to choose merge/delete options
				this.updateModal = {
					category: cat,
					postCount: count,
					categoryOptions: _.sortBy(_.without(this.categories, cat), 'name'),
					withDeleted: 'keep',
					tagWith: '',
					type,
				};
			} else {
				// merging or deleting this category will affect no posts
				let confirm;
				if (type == 'merge') {
					confirm = `No posts found. Would you like to merge <b>${_.escape(cat.name)}</b>?`;
				} else if (type == 'delete') {
					confirm = `No posts found. Are you sure you want to delete <b>${_.escape(cat.name)}</b>?`;
				}
				this.modal.confirm({ escapedHtml: confirm }).then(
					() => {
						// hide category from display
						spliceOut(this.categories, cat);
						// run the delete
						this.Api.delete(`/categories/${cat.id}`);
					},
					() => {}
				);
			}
		} catch (e) {
			console.error(e);
			modal.close();
			this.modal.alert({ text: 'Error fetching category information. Please try again.' }).catch(() => {});
		}
	}

	cancelUpdate() {
		this.updateModal = null;
	}

	async doMerge() {
		// read the chosen merge options
		const { category, keepId, tagWith } = this.updateModal;
		// require that a new category be chosen
		if (!keepId) {
			this.modal.alert({ text: 'Please choose a category.' }).catch(() => {});
			return;
		}
		// get the "please wait" message
		let msg;
		if (this.updateModal.postCount > 2000) {
			msg = 'Merging may take a minute. You may close this message at any time.';
		} else {
			msg = 'Merging may take a few moments. You may close this message at any time.';
		}
		// close update modal
		this.updateModal = null;
		// show the message
		this.modal.alert({ text: msg }).catch(() => {});
		// kick off merge process
		this.Api.post(`/categories/merge`, {
			keep: keepId,
			discard: category.id,
			tag_with: tagWith,
		});
		// remove category from display
		spliceOut(this.categories, category);
	}

	async doDelete() {
		// read the chosen delete options
		const { category, tagWith, withDeleted, insteadApply } = this.updateModal;
		// get the "please wait" message
		let msg;
		if (this.updateModal.postCount > 2000) {
			msg = 'Deleting may take a minute. You may close this message at any time.';
		} else {
			msg = 'Deleting may take a few moments. You may close this message at any time.';
		}
		// close update modal
		this.updateModal = null;
		// show the message
		this.modal.alert({ text: msg }).catch(() => {});
		// which "instead category did they choose"
		if (withDeleted === 'keep') {
			// Keep existing: kick off the delete process
			this.Api.delete(`/categories/${category.id}`);
		} else {
			// uncategorize or replace with another category (which is the same as merge)
			this.Api.post(`/categories/merge`, {
				keep: withDeleted === 'uncategorize' ? 0 : insteadApply,
				discard: category.id,
				tag_with: tagWith,
			});
		}
		// remove category from display
		spliceOut(this.categories, category);
	}
}
